import React, { Component } from 'react'
import { observer } from 'mobx-react'
import classNames from 'classnames'
import styled, { css } from 'styled-components'
import { lighten } from 'polished'
import media from '@Styles/media'
import { HubText } from 'src/common/rtl/HubWidgets'
import { HubInput, HubTextarea } from 'src/common/rtl/HubWidgets'

@observer
class TextInput extends Component {
  state = {
    paddingLeft: 9,
    paddingRight: 9,
  }

  _before = null
  _after = null
  _inputRef = null

  componentDidMount() {
    this.adjustPadding()
    this.autoSelect()
  }

  UNSAFE_componentWillReceiveProps() {
    this.adjustPadding()
  }

  adjustPadding() {
    const { prependMargin = 9, appendMargin = 9 } = this.props
    let paddingLeft = 9
    let paddingRight = 9
    if (this._before) {
      const node = this._before
      if (node instanceof HTMLElement) {
        // Adjusting for size 18px icons by default. For some reason it returns offsetWidth of 0.
        paddingLeft = paddingLeft + (node.offsetWidth || 18) + prependMargin
      }
    }
    if (this._after) {
      const node = this._after
      if (node instanceof HTMLElement) {
        // Adjusting for size 18px icons by default. For some reason it returns offsetWidth of 0.
        paddingRight = paddingRight + (node.offsetWidth || 18) + appendMargin
      }
    }

    this.setState({ paddingLeft, paddingRight })
  }

  autoSelect() {
    const { autoSelect } = this.props
    const { _inputRef } = this
    autoSelect && _inputRef && _inputRef.select()
  }

  onChange = (evt) => {
    const { onChange, type } = this.props
    if (!onChange) {
      return
    }
    if (type === 'number') {
      onChange({
        ...evt,
        target: {
          ...evt.target,
          value: evt.target.value ? Number(evt.target.value) : '',
        },
      })
    } else {
      onChange(evt)
    }
  }

  render() {
    const {
      rows,
      error,
      errorType,
      prepend,
      append,
      className,
      groupClassName,
      style,
      category,
      ...restProps
    } = this.props
    const { paddingLeft, paddingRight } = this.state

    const before = prepend ? <Prepend ref={(r) => (this._before = r)}>{prepend()}</Prepend> : null

    const after = append ? <Append ref={(r) => (this._after = r)}>{append()}</Append> : null

    const combinedStyle = {
      paddingLeft,
      paddingRight,
      ...style,
    }

    return (
      <InputGroup category={category} className={classNames(groupClassName ? groupClassName : '')}>
        {before}
        <InputContainer error={error} errorType={errorType}>
          {rows < 2 ? (
            <StyledInput
              error={error}
              errorType={errorType}
              ref={(r) => (this._inputRef = r)}
              style={combinedStyle}
              className={className}
              {...restProps}
              onChange={this.onChange}
            />
          ) : (
            <StyledTextArea
              error={error}
              errorType={errorType}
              style={combinedStyle}
              className={className}
              rows={rows}
              {...restProps}
              onChange={this.onChange}
            />
          )}
          {error ? <ErrorMessage errorType={errorType}>{error}</ErrorMessage> : null}
        </InputContainer>
        {after}
      </InputGroup>
    )
  }
}

TextInput.defaultProps = {
  rows: 1,
  size: 'normal',
  errorType: 'error',
}

const ErrorMessage = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.fontSizeSmaller};
  color: ${({ errorType, theme }) =>
    errorType === 'warn' ? theme.colors.warningColor : theme.colors.dangerColor};
  margin-top: 3px;
  line-height: 1.2;
  font-weight: 500;
  min-height: 18px;

  a {
    color: white !important;
    text-decoration: underline;
  }
`

const ICON_SIZE = '18px'

const styledInput = (input) => styled(input`
  display: block;
  width: 100%;
  padding: ${({ theme }) => theme.sizes.gutterSmall};
  background-color: white;
  border: 1px solid ${({ theme }) => theme.colors.grayLight};
  border-radius: 3px;
  font-size: ${({ theme }) => theme.fontSizes.fontSizeSmall};
  line-height: 1.5em;
  height: 36px;
  resize: none;

  &::placeholder {
    color: ${({ theme }) => theme.colors.grayLight};
  }

  appearance: none;
  box-shadow: none;
  outline: none;

  &[rows] {
    height: auto;
  }
`)`
  &:focus {
    border-color: ${({ errorType, error, theme }) =>
      !error
        ? theme.colors.primaryAccentColor
        : errorType === 'warn'
        ? theme.colors.warningBgColor
        : theme.colors.dangerBgColor};
    transition: border 500ms linear;
  }

  &:disabled {
    cursor: default;
    background-color: #f7f7f7;
  }

  ${({ error, errorType, theme }) =>
    error
      ? css`
          border: 1px solid
            ${errorType === 'warn' ? theme.colors.warningBgColor : theme.colors.dangerColor};
          background-color: ${lighten(
            0.15,
            errorType === 'warn' ? theme.colors.warningBgColor : theme.colors.dangerBgColor
          )};
        `
      : ''}

  ${({ before, theme }) =>
    before
      ? css`padding-left: calc(${theme.sizes.gutterSmall} + ${ICON_SIZE} + ${theme.sizes.gutterSmall}); }`
      : ''}

  ${({ after, theme }) =>
    after
      ? css`
          padding-right: calc(
            ${theme.sizes.gutterSmall} + ${ICON_SIZE} + ${theme.sizes.gutterSmall}
          );
        `
      : ''}
`

const StyledTextArea = styledInput(styled(HubTextarea))
const StyledInput = styledInput(styled(HubInput))

const Prepend = styled.span`
  position: absolute;
  top: 6px;
  left: ${({ theme }) => theme.sizes.gutterSmall};
  z-index: 10;
`

const Append = styled.span`
  position: absolute;
  top: 6px;
  right: ${({ theme }) => theme.sizes.gutterSmall};
  z-index: 10;
`

const InputGroup = styled(styled.span`
  display: inline-block;
  position: relative;
  width: 100%;
  font-size: ${({ theme }) => theme.fontSizes.fontSizeSmall};
  line-height: 1.7em;

  .px-icon {
    font-size: ${ICON_SIZE};
  }

  a {
    color: ${({ theme }) => theme.colors.primaryAccentColor};
  }
`)`
  ${({ category, theme }) => {
    switch (category) {
      case 'underline':
        return css`
          ${Append} {
            right: calc(${theme.sizes.baseSize} / 3);
          }

          ${StyledInput}, ${StyledTextArea} {
            padding: calc(${theme.sizes.gutterSmall} / 2) 0 !important;
            border: none;
            border-bottom: 1px solid ${theme.colors.grayLight};
            border-radius: 0;
            font-weight: 300;
            font-size: 15px;
            font-size: ${theme.fontSizes.fontSizeSmall};
            line-height: ${theme.sizes.gutterLarge};
            background-color: transparent;

            &:hover {
              border-color: ${theme.colors.grayMedium};
            }

            &:focus {
              outline: none;
              border: none;
              border-bottom: 1px solid ${theme.colors.primaryAccentColor};
            }
          }
        `
      default:
        return ''
    }
  }};
`

const InputContainer = styled.div`
  width: 100%;
  position: relative;

  ${({ size, theme }) =>
    size === 'large'
      ? `padding: ${theme.sizes.gutterSmallest}${theme.sizes.gutterMedium};`
      : ''} ${media.smallScreen`
  ${StyledTextArea}, ${StyledInput}, ${Prepend}, ${Append} {
    font-size: ${({ theme }) => theme.fontSizes.fontSizeSmaller};
  }`};
`

export default TextInput
